import { useEffect, useState } from "react";
import { motion, animate, AnimatePresence, Variants } from "framer-motion";
import ConfirmDeletionLogo from "assets/imgs/ConfirmDeleteImage.svg";
import CircularLoadingDots from "components/utils/CircularLoadingDots";
import FuseCountDownButton from "components/utils/FuseCountDownButton";

interface ConfirmDeletionPanelProps {
  title: string;
  paragraphs: string[];
  deleteText: string;
  onDelete: () => Promise<void>;
  onClose: () => void;
}

/**
 * Definition of the variants of this component
 */
const blurredBackgroundVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut", delay: 0.4 },
  },
};
const modalVariants: Variants = {
  initial: { opacity: 0, y: 400 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeInOut" },
  },
  exit: {
    opacity: 0,
    y: 700,
    transition: { duration: 1, ease: "easeOut" },
  },
};

const ConfirmDeletionPanel: React.FC<ConfirmDeletionPanelProps> = ({
  title,
  paragraphs,
  deleteText,
  onDelete,
  onClose,
}) => {
  // States definition
  const [stage, setStage] = useState(1);
  const [timer, setTimer] = useState(100);
  const [triggerTimer, setTriggerTimer] = useState(false);

  /**
   * Function that executes the delete routine once the
   * process is at stage 2
   */
  useEffect(() => {
    if (stage === 2) {
      // Execute the delete function
      onDelete().then((response) => {
        // Once finished the execution, go to the stage 3
        setStage(3);
      });
    }
  }, [stage]);

  /**
   * Function that controls the progress bar animation
   * on the dismiss step (stage 3)
   */
  useEffect(() => {
    if (triggerTimer) {
      const controls = animate(100, 0, {
        duration: 5,
        ease: "linear",
        onUpdate: (latest: number) => {
          // Set the value of the timer
          setTimer(latest);
        },
        onComplete: () => onClose(),
      });
      return () => controls.stop();
    }
  }, [triggerTimer]);

  /**
   * Function that prevents closing the modal once the
   * process is started
   * @returns
   */
  const handleClickBackground = () => {
    // Prevent going out when started the process
    if (stage > 1) return;

    // Otherwise, execute the onClose function
    onClose();
  };

  /**
   * Function used when clicking the delete function
   */
  const handleClickDelete = () => {
    // And go into the next stage
    setStage(2);
  };

  return (
    <>
      <motion.div
        key="background"
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={blurredBackgroundVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClickBackground}
      />
      <motion.div
        key="modal"
        layout
        className="fixed w-[440px] left-0 right-0 ml-auto mr-auto origin-bottom p-6 bottom-5 bg-white rounded-3xl z-30 overflow-hidden inline-flex gap-4"
        variants={modalVariants}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.7, ease: "easeOut" }}
        exit="exit"
      >
        <AnimatePresence mode="wait">
          {stage === 1 ? (
            <motion.div
              key="init"
              exit={{ x: "-100%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="inline-flex w-full max-h-[80vh] flex-col flex-1 justify-start items-center gap-10 overflow-y-auto custom-scroll"
            >
              <div className="self-stretch px-3.5 py-4 justify-center items-center inline-flex">
                <img
                  alt="delete"
                  src={ConfirmDeletionLogo}
                  className="w-[229px] h-[128px] select-none"
                />
              </div>
              <div className="w-full h-fit flex-col justify-start items-start gap-6 flex">
                <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
                  <div className="self-stretch text-neutral-700 text-[23px] leading-7 font-semibold font-sans justify-start select-none">
                    {title}
                  </div>
                  <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
                    {paragraphs &&
                      paragraphs.length > 0 &&
                      paragraphs.map((paragraph: string, index: number) => (
                        <div
                          key={index}
                          className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-7 select-none"
                        >
                          {paragraph}
                        </div>
                      ))}
                  </div>
                </div>
                <div
                  className="self-stretch h-[46px] px-5 py-3 bg-error-500 hover:bg-error-600 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                  onClick={handleClickDelete}
                >
                  <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                    {deleteText}
                  </div>
                </div>
                <div className="self-stretch h-[46px] px-5 py-3 rounded justify-center items-center gap-2 inline-flex">
                  <div
                    className="text-secondary-600 hover:underline text-lg font-semibold font-sans leading-snug cursor-pointer select-none"
                    onClick={onClose}
                  >
                    Go back
                  </div>
                </div>
              </div>
            </motion.div>
          ) : stage === 2 ? (
            <motion.div
              key="loading"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{
                x: "-100%",
                transition: { duration: 0.3, ease: "easeOut" },
              }}
              transition={{ delay: 0.1, duration: 0.3, ease: "easeOut" }}
              className="inline-flex w-full max-h-[80vh] flex-col flex-1 justify-start items-center gap-10 overflow-y-auto custom-scroll"
            >
              <div className="self-stretch px-3.5 py-4 justify-center items-center inline-flex">
                <CircularLoadingDots
                  nDots={10}
                  className="relative w-32 h-32"
                />
              </div>
              <div className="w-full h-fit flex-col justify-start items-start gap-6 flex">
                <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
                  <div className="self-stretch text-neutral-700 text-[23px] leading-7 font-semibold font-sans justify-start select-none">
                    Deletion in Progress
                  </div>
                  <div className="self-stretch inline-flex flex-col justify-start items-start gap-2">
                    <div className="self-stretch text-neutral-700 text-base font-normal font-sans leading-7 select-none">
                      Please wait while we process this request...
                    </div>
                  </div>
                </div>
                <div className="self-stretch h-[46px] px-5 py-3 bg-Subtle rounded justify-center items-center gap-2 inline-flex cursor-not-allowed">
                  <div className="text-neutral-400 text-lg font-semibold font-sans leading-snug select-none">
                    {deleteText}
                  </div>
                </div>
                <div className="self-stretch h-[46px] px-5 py-3 rounded justify-center items-center gap-2 inline-flex">
                  <div className="text-Subtle text-lg font-semibold font-sans leading-snug cursor-default select-none">
                    Go back
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="complete"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{
                x: "-100%",
                transition: { duration: 0.3, ease: "easeOut" },
              }}
              transition={{ delay: 0.1, duration: 0.3, ease: "easeOut" }}
              className="inline-flex w-full max-h-[80vh] flex-col flex-1 justify-start items-center gap-10 overflow-y-auto custom-scroll"
              onAnimationComplete={() => setTriggerTimer(true)}
            >
              <div className="w-full h-fit flex-col justify-start items-start gap-6 flex">
                <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
                  <div className="self-stretch text-neutral-700 text-[23px] leading-7 font-semibold font-sans justify-start select-none">
                    Deletion Complete
                  </div>
                  <div className="self-stretch text-neutral-700 text-base font-normal font-sans leading-7 select-none">
                    Deletion completed successfully
                  </div>
                </div>
                {/* Count down buttons */}
                <FuseCountDownButton
                  text="Dismiss"
                  timer={timer}
                  onClick={onClose}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default ConfirmDeletionPanel;
